/**
 * Shared filter state for the Global View "Filters" popup
 * (`PosFilterModal.tsx`). Previously lived inside `V2PosFilters.tsx`
 * (a duplicate, never-rendered card-style filter panel) — moved here when
 * that dead component was deleted, so the type/default aren't tied to any
 * one UI implementation.
 */

export interface FilterOption {
  value: string | number;
  label: string;
}

export type MarketSelectionContext = 'off' | 'on_premium' | 'on_full';

export const getMarketSelectionContext = (
  channel: 'on_trade' | 'off_trade',
  contentMode: 'premium' | 'full'
): MarketSelectionContext => {
  if (channel === 'off_trade') return 'off';
  return contentMode === 'premium' ? 'on_premium' : 'on_full';
};

export const getMarketsForContext = (
  filters: PosFilterState,
  context: MarketSelectionContext
): FilterOption[] => filters.marketSelections?.[context] ?? filters.markets;

export const setMarketsForContext = (
  filters: PosFilterState,
  context: MarketSelectionContext,
  markets: FilterOption[]
): PosFilterState => ({
  ...filters,
  markets,
  marketSelections: {
    ...filters.marketSelections,
    [context]: markets
  }
});

export interface PosFilterState {
  markets: FilterOption[];
  marketSelections?: Partial<Record<MarketSelectionContext, FilterOption[]>>;
  continentId: string;
  countryId: string;
  wineColor: string;
  wineGrapeVariety: FilterOption[];
  wineCountry: string;
  wineRegion: string;
  wineFormatGroup: string;
  // Off-trade spirits only: bottle volumes in ml (wine_format.size), e.g.
  // '700'. On trade uses `wineFormatGroup` (glass / cocktail / bottle).
  spiritFormatSize: FilterOption[];
  wineAppelation: string;
  // Spirits-only equivalents of the wine-only fields above — shown instead
  // of them in the Product segment when the header's Spirits toggle is on
  // (see `V2FiltersContext`'s `productType`). Backed by real DB data
  // (wine_sub_type.type_id=2, spirit_countries) just like their wine
  // counterparts; reset to 'all' whenever the category toggle flips so a
  // hidden field never silently filters results (see
  // `resetProductTypeFields`).
  spiritCategory: string;
  spiritCountry: string;
  vintage: FilterOption[];
  clientRefs: FilterOption[];
  clientCustomRefs: FilterOption[];
  storePriceBand: FilterOption[];
  storePrimaryType: FilterOption[];
  storeWsAward: FilterOption[];
  storeMichelinStar: FilterOption[];
  // Spirits-only store awards (store_restaurant.worlds_best / top_bar /
  // spirited_award); values mirror the DB: Top50|Top100|50discovery, Top500,
  // Winner.
  storeWorldsBest: FilterOption[];
  storeTopBar: FilterOption[];
  storeSpiritedAward: FilterOption[];
  posStatus: string;
  surveyYear: string;
  surveyYearSelections?: Partial<Record<MarketSelectionContext, string>>;
  rangePrices: [number, number];
  search: string;
}

// Bounds of the "Median price per POS" filter — kept in sync with
// DEFAULT_FILTERS.rangePrices so an untouched range never counts as active.
export const PRICE_RANGE_BOUNDS: [number, number] = [0, 10000];

export const DEFAULT_FILTERS: PosFilterState = {
  markets: [],
  marketSelections: {
    off: [],
    on_premium: [],
    on_full: []
  },
  continentId: 'all',
  countryId: 'all',
  wineColor: 'all',
  wineGrapeVariety: [],
  wineCountry: 'all',
  wineRegion: 'all',
  wineFormatGroup: 'all',
  spiritFormatSize: [],
  wineAppelation: 'all',
  spiritCategory: 'all',
  spiritCountry: 'all',
  vintage: [],
  clientRefs: [],
  clientCustomRefs: [],
  storePriceBand: [],
  storePrimaryType: [],
  storeWsAward: [],
  storeMichelinStar: [],
  storeWorldsBest: [],
  storeTopBar: [],
  storeSpiritedAward: [],
  posStatus: 'all',
  surveyYear: 'all',
  surveyYearSelections: {},
  rangePrices: [PRICE_RANGE_BOUNDS[0], PRICE_RANGE_BOUNDS[1]],
  search: ''
};

/**
 * Single source of truth for the "N filters applied" badge shown next to
 * every entry point into the POS filter popup (Global View, Points of Sale,
 * CRM Sales Tracking, and the popup itself). Restaurant-only criteria
 * (price band, cuisine, awards) only count while on trade.
 */
export const OFF_TRADE_SPIRIT_POS_TYPES: FilterOption[] = [
  'Specialty Retailer',
  'Chain',
  'Wine & Spirits Shop',
  'Spirits Shop',
  'Wine Cellar/Restaurant',
  'Private Club'
].map((name) => ({ value: name, label: name }));

// value = wine_format.size (ml), sent as `wine_format_size`.
export const OFF_TRADE_SPIRIT_FORMATS: FilterOption[] = [
  { value: '375', label: '375ml' },
  { value: '500', label: '500ml' },
  { value: '700', label: '700ml' },
  { value: '750', label: '750ml' },
  { value: '1000', label: '1L' },
  { value: '1500', label: '1.5L' },
  { value: '1750', label: '1.75L' }
];

// Only numeric ids may be sent as numeric query params.
export const toNumericIds = (options: FilterOption[]): number[] | null => {
  const ids = options
    .map((o) => Number(o.value))
    .filter((n) => Number.isFinite(n));
  return ids.length > 0 ? ids : null;
};

const toFiniteNumberOrNull = (value: string): number | null => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const countActiveFilters = (
  filters: PosFilterState,
  isOnTrade: boolean,
  isSpirits = false
): number => {
  let count = 0;
  if (filters.continentId !== 'all') count++;
  if (filters.countryId !== 'all') count++;
  if (filters.markets.length > 0) count++;
  if (filters.wineColor !== 'all') count++;
  if (filters.wineGrapeVariety.length > 0) count++;
  if (filters.wineCountry !== 'all') count++;
  if (filters.wineRegion !== 'all') count++;
  if (filters.wineFormatGroup !== 'all') count++;
  if (isSpirits && filters.spiritFormatSize.length > 0) count++;
  if (filters.wineAppelation !== 'all') count++;
  if (filters.spiritCategory !== 'all') count++;
  if (filters.spiritCountry !== 'all') count++;
  if (filters.vintage.length > 0) count++;
  if (filters.clientRefs.length > 0) count++;
  if (filters.clientCustomRefs.length > 0) count++;
  if (isOnTrade && filters.storePriceBand.length > 0) count++;
  if ((isOnTrade || isSpirits) && filters.storePrimaryType.length > 0) count++;
  if (
    isOnTrade &&
    (filters.storeWsAward.length > 0 ||
      filters.storeMichelinStar.length > 0 ||
      filters.storeWorldsBest.length > 0 ||
      filters.storeTopBar.length > 0 ||
      filters.storeSpiritedAward.length > 0)
  )
    count++;
  if (filters.posStatus !== 'all') count++;
  if (
    filters.rangePrices[0] !== PRICE_RANGE_BOUNDS[0] ||
    filters.rangePrices[1] !== PRICE_RANGE_BOUNDS[1]
  )
    count++;
  return count;
};

/**
 * Resets whichever Product-segment fields don't apply to the newly-selected
 * category when the Wine/Spirits header toggle flips (mirrors proto's
 * gvWireCategoryToggle reset list). Necessary because:
 *  - the wine-only fields (color/grape/country/region/appellation) and
 *    spirits-only fields (category of spirit/country of origin) are hidden
 *    rather than removed, so a stale selection would otherwise keep
 *    silently filtering results the user can no longer see or clear;
 *  - `wineFormatGroup` stores a raw id whose meaning (wine_format_group vs
 *    spirit_format_group) depends on category — the two id spaces overlap,
 *    so a leftover value could silently apply the wrong format filter.
 */
export const resetProductTypeFields = (
  filters: PosFilterState,
  isSpirits: boolean
): PosFilterState => ({
  ...filters,
  wineColor: isSpirits ? DEFAULT_FILTERS.wineColor : filters.wineColor,
  wineGrapeVariety: isSpirits
    ? DEFAULT_FILTERS.wineGrapeVariety
    : filters.wineGrapeVariety,
  wineCountry: isSpirits ? DEFAULT_FILTERS.wineCountry : filters.wineCountry,
  wineRegion: isSpirits ? DEFAULT_FILTERS.wineRegion : filters.wineRegion,
  wineAppelation: isSpirits
    ? DEFAULT_FILTERS.wineAppelation
    : filters.wineAppelation,
  spiritCategory: isSpirits
    ? filters.spiritCategory
    : DEFAULT_FILTERS.spiritCategory,
  spiritCountry: isSpirits
    ? filters.spiritCountry
    : DEFAULT_FILTERS.spiritCountry,
  wineFormatGroup: DEFAULT_FILTERS.wineFormatGroup,
  spiritFormatSize: isSpirits
    ? filters.spiritFormatSize
    : DEFAULT_FILTERS.spiritFormatSize,
  // Awards are category-specific: WS / Michelin for wine, the three spirits
  // awards for spirits.
  storeWsAward: isSpirits ? DEFAULT_FILTERS.storeWsAward : filters.storeWsAward,
  storeMichelinStar: isSpirits
    ? DEFAULT_FILTERS.storeMichelinStar
    : filters.storeMichelinStar,
  storeWorldsBest: isSpirits
    ? filters.storeWorldsBest
    : DEFAULT_FILTERS.storeWorldsBest,
  storeTopBar: isSpirits ? filters.storeTopBar : DEFAULT_FILTERS.storeTopBar,
  storeSpiritedAward: isSpirits
    ? filters.storeSpiritedAward
    : DEFAULT_FILTERS.storeSpiritedAward
});

const toStringValuesOrNull = (options: FilterOption[]): string[] | null =>
  options.length > 0 ? options.map((o) => String(o.value)) : null;

/** Query params for the spirits-only store award filters. */
export const buildSpiritAwardQueryFields = (
  filters: PosFilterState
): {
  store_worlds_best: string[] | null;
  store_top_bar: string[] | null;
  store_spirited_award: string[] | null;
} => ({
  store_worlds_best: toStringValuesOrNull(filters.storeWorldsBest),
  store_top_bar: toStringValuesOrNull(filters.storeTopBar),
  store_spirited_award: toStringValuesOrNull(filters.storeSpiritedAward)
});

/**
 * Maps the Product-segment draft onto the wine_/spirit_ query params the
 * `/global_data_view` and `/v2/distribution` endpoints expect — both sets
 * are real, already-wired backend filters (wine_sub_type.type_id=2 for
 * category of spirit, spirit_format_group_id for Glass/Cocktail/Bottle),
 * not simulated data. Centralised so Key Indicators / Market Overview /
 * Raw Data in `GlobalView.tsx` build this block identically.
 */
export const buildProductTypeQueryFields = (
  filters: PosFilterState,
  isSpirits: boolean
): {
  wine_color_id: number | null;
  wine_sub_type_id: number | null;
  wine_country_id: number | null;
  wine_region_id: number | null;
  wine_appelation_id: number[] | null;
  wine_grape_variety_id: number[] | null;
  wine_format_group_id: number | null;
  spirit_format_group_id: number | null;
  wine_format_size: string[] | null;
} => ({
  wine_color_id:
    !isSpirits && filters.wineColor !== 'all'
      ? Number(filters.wineColor)
      : null,
  wine_sub_type_id:
    isSpirits && filters.spiritCategory !== 'all'
      ? Number(filters.spiritCategory)
      : null,
  wine_country_id: isSpirits
    ? filters.spiritCountry !== 'all'
      ? Number(filters.spiritCountry)
      : null
    : filters.wineCountry !== 'all'
      ? Number(filters.wineCountry)
      : null,
  wine_region_id:
    !isSpirits && filters.wineRegion !== 'all'
      ? Number(filters.wineRegion)
      : null,
  wine_appelation_id:
    !isSpirits && filters.wineAppelation !== 'all'
      ? [Number(filters.wineAppelation)]
      : null,
  wine_grape_variety_id:
    !isSpirits && filters.wineGrapeVariety.length > 0
      ? filters.wineGrapeVariety.map((g) => Number(g.value))
      : null,
  wine_format_group_id:
    !isSpirits && filters.wineFormatGroup !== 'all'
      ? toFiniteNumberOrNull(filters.wineFormatGroup)
      : null,
  spirit_format_group_id:
    isSpirits && filters.wineFormatGroup !== 'all'
      ? toFiniteNumberOrNull(filters.wineFormatGroup)
      : null,
  wine_format_size:
    isSpirits && filters.spiritFormatSize.length > 0
      ? filters.spiritFormatSize.map((o) => String(o.value))
      : null
});

export const filtersForChannel = (
  filters: PosFilterState,
  channel: 'on_trade' | 'off_trade',
  isSpirits = false
): PosFilterState => {
  const offTradeSpirits = channel === 'off_trade' && isSpirits;
  const offPosTypes = new Set(OFF_TRADE_SPIRIT_POS_TYPES.map((o) => o.value));
  // Off-trade spirits filter on volume (spiritFormatSize), on trade on the
  // format group; the other channel's selection is dropped.
  const wineFormatGroup = offTradeSpirits ? 'all' : filters.wineFormatGroup;
  if (channel === 'on_trade') {
    const unchanged =
      filters.spiritFormatSize.length === 0 &&
      filters.storePrimaryType.every((o) => !offPosTypes.has(String(o.value)));
    if (unchanged) return filters;
    return {
      ...filters,
      storePrimaryType: filters.storePrimaryType.filter(
        (o) => !offPosTypes.has(String(o.value))
      ),
      spiritFormatSize: []
    };
  }
  return {
    ...filters,
    storePriceBand: [],
    storePrimaryType: offTradeSpirits
      ? filters.storePrimaryType.filter((o) => offPosTypes.has(String(o.value)))
      : [],
    storeWsAward: [],
    storeMichelinStar: [],
    storeWorldsBest: [],
    storeTopBar: [],
    storeSpiritedAward: [],
    wineFormatGroup,
    spiritFormatSize: offTradeSpirits ? filters.spiritFormatSize : []
  };
};

export const getSurveyYearForContext = (
  filters: PosFilterState,
  context: MarketSelectionContext,
  years: string[]
): string => {
  const selected =
    filters.surveyYearSelections?.[context] ??
    (filters.surveyYearSelections || context === 'off'
      ? 'all'
      : filters.surveyYear);
  if (selected !== 'all' && years.includes(selected)) return selected;
  return context === 'off' ? years[0] ?? '' : 'all';
};

export const setSurveyYearForContext = (
  filters: PosFilterState,
  context: MarketSelectionContext,
  year: string
): PosFilterState => ({
  ...filters,
  surveyYear: year,
  surveyYearSelections: { ...filters.surveyYearSelections, [context]: year }
});
